import {
  Query,
  CollectionReference,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  QueryConstraint,
  QueryDocumentSnapshot,
  WhereFilterOp
} from 'firebase/firestore';

export interface RangeFilter {
  min?: number;
  max?: number;
  start?: Date;
  end?: Date;
}

export interface SortOption {
  field: string;
  direction: 'asc' | 'desc';
}

export class FirestoreQueryBuilder {
  private constraints: QueryConstraint[] = [];
  private baseQuery: Query | CollectionReference;

  constructor(baseQuery: Query | CollectionReference) {
    this.baseQuery = baseQuery;
  }

  where(field: string, op: WhereFilterOp, value: any): FirestoreQueryBuilder {
    if (value !== undefined && value !== null) {
      // For array 'in' queries, ensure array is not empty
      if ((op === 'in' || op === 'not-in' || op === 'array-contains-any') && Array.isArray(value) && value.length === 0) {
        return this;
      }
      this.constraints.push(where(field, op, value));
    }
    return this;
  }

  orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): FirestoreQueryBuilder {
    this.constraints.push(orderBy(field, direction));
    return this;
  }

  paginate(limitCount: number, lastDoc?: QueryDocumentSnapshot | null): FirestoreQueryBuilder {
    if (lastDoc) {
      this.constraints.push(startAfter(lastDoc));
    }
    this.constraints.push(limit(limitCount));
    return this;
  }

  build(): Query {
    return query(this.baseQuery, ...this.constraints);
  }

  // Helper for range filters
  // Note: Firestore allows range filters on only one field per query
  addRangeFilter(field: string, range?: RangeFilter): FirestoreQueryBuilder {
    if (!range) return this;

    if (range.min !== undefined) {
      this.constraints.push(where(field, '>=', range.min));
    }
    if (range.max !== undefined) {
      this.constraints.push(where(field, '<=', range.max));
    }
    if (range.start !== undefined) {
      this.constraints.push(where(field, '>=', Timestamp.fromDate(range.start)));
    }
    if (range.end !== undefined) {
      this.constraints.push(where(field, '<=', Timestamp.fromDate(range.end)));
    }
    return this;
  }
}
